import { useEffect, useMemo, useRef, useState } from "react";
import Topbar from "../components/Topbar";
import { listHotels } from "../api/hotels";
import { getCompany } from "../api/companies";
import { HotelOut, HotelType } from "../types/hotels";
import { useSearchParams, Link } from "react-router-dom";
import { Building2, ExternalLink, MapPin } from "lucide-react";
import { useAuth } from "../context/AuthContext";

import SearchInput from "../components/controls/SearchInput";
import SelectField from "../components/controls/SelectField";
import FilterBar from "../components/controls/FilterBar";
import AddHotelModal from "../components/hotels/AddHotelModal";


const TYPE_OPTIONS: HotelType[] = ["Отель","Сетевой отель","Апартаменты","Гостевой дом","Коттедж, вилла"];

export default function CompanyHotelsPage() {
  const { company_id: companyId } = useAuth();

  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<HotelOut[]>([]);
  const [page, setPage] = useState<number>(Number(searchParams.get("page") || 1));
  const perPageInit = Number(searchParams.get("per_page") || 20);
  const [perPage, setPerPage] = useState<number>(Number.isFinite(perPageInit) ? perPageInit : 20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [otaLimit, setOtaLimit] = useState<number | null>(null);

  const [openAdd, setOpenAdd] = useState(false);

  const qInit = searchParams.get("q") || "";
  const cityInit = searchParams.get("city") || "";
  const typeInit = (searchParams.get("type") as HotelType) || "" as any;

  const [qDraft, setQDraft] = useState(qInit);
  const [cityDraft, setCityDraft] = useState(cityInit);
  const [typeDraft, setTypeDraft] = useState<HotelType | "">(typeInit);

  const [q, setQ] = useState(qInit);
  const [city, setCity] = useState(cityInit);
  const [type, setType] = useState<HotelType | "">(typeInit);

  const cityOptions = useMemo(() => {
    const set = new Set(items.map(i => i.city).filter(Boolean));
    return Array.from(set).sort((a,b)=>a.localeCompare(b));
  }, [items]);

  const typeOptions = TYPE_OPTIONS.map(v => ({ label: v, value: v }));
  const citySelectOptions = cityOptions.map(c => ({ label: c, value: c }));

  const timerRef = useRef<number | null>(null);
  function scheduleUpdateURL() {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      const next = new URLSearchParams();
      if (q) next.set("q", q);
      if (city) next.set("city", city);
      if (type) next.set("type", type as string);
      next.set("page", String(page));
      setSearchParams(next);
    }, 350);
  }

  useEffect(() => {
    if (!companyId) return;
    setLoading(true); setError("");
    (async () => {
      try {
        const data = await listHotels(companyId, {
          page,
          per_page: perPage,
          q: q || null,
          city: city || null,
          type: (type as HotelType) || null,
        });
        setItems(data.items);
        setTotal(data.total);
      } catch (e: any) {
        setError(e?.response?.data?.detail || "Ошибка загрузки списка");
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId, page, q, city, type, perPage]);

  useEffect(() => {
    if (!companyId) {
      setOtaLimit(null);
      return;
    }

    getCompany(companyId)
      .then((company) => setOtaLimit(company.ota_count ?? null))
      .catch(() => setOtaLimit(null));
  }, [companyId]);

  function applyFilters() {
    setQ(qDraft);
    setCity(cityDraft);
    setType(typeDraft);

    const next = new URLSearchParams();
    if (qDraft) next.set("q", qDraft);
    if (cityDraft) next.set("city", cityDraft);
    if (typeDraft) next.set("type", typeDraft as string);
    next.set("page", "1");
    setPage(1);
    setSearchParams(next);
  }

  function resetFilters() {
    setQDraft("");
    setCityDraft("");
    setTypeDraft("");

    setQ("");
    setCity("");
    setType("");

    setPage(1);
    setSearchParams(new URLSearchParams());
  }

  function changePerPage(n: number) {
    setPerPage(n);
    setPage(1);

    const next = new URLSearchParams(searchParams);
    if (n) next.set("per_page", String(n)); else next.delete("per_page");
    next.set("page", "1");

    if (q) next.set("q", q); else next.delete("q");
    if (city) next.set("city", city); else next.delete("city");
    if (type) next.set("type", String(type)); else next.delete("type");

    setSearchParams(next);
  }

  function reloadCurrent() {
  if (!companyId) return;
  setLoading(true);
  listHotels(companyId, { page, per_page: perPage, q: q || null, city: city || null, type: (type as HotelType) || null })
    .then((d)=>{ setItems(d.items); setTotal(d.total); })
    .catch((e:any)=> setError(e?.response?.data?.detail || "Ошибка загрузки списка"))
    .finally(()=> setLoading(false));
}

  useEffect(() => { scheduleUpdateURL(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [q, city, type, page]);

  const pages = Math.max(1, Math.ceil(total / perPage));
  const hasActiveFilters = Boolean(q || city || type);
  const rangeStart = total ? (page - 1) * perPage + 1 : 0;
  const rangeEnd = total ? Math.min(page * perPage, total) : 0;
  const stats = [
    { label: "Всего объектов", value: total ? total.toLocaleString("ru-RU") : "—", desc: "в базе компании" },
    { label: "Максимум ОТА", value: otaLimit ?? "—", desc: "ссылок на каждый объект" },
    { label: "Фильтр по городу", value: city || "Все города", desc: city ? "активный фильтр" : "без фильтра" },
    { label: "Тип размещения", value: type || "Все типы", desc: type ? "активный фильтр" : "любой формат" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-900">
      <Topbar />

      <main className="mx-auto w-full max-w-[1400px] px-6 py-10">
        <section className="mb-10 space-y-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between animate-softFade">
            <div>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">Мои объекты размещения</h1>
              <p className="mt-2 text-slate-500">
                {hasActiveFilters ? "Показываем результаты по выбранным фильтрам" : "Все объекты вашей компании"}
                {typeof total === "number" ? ` · ${total} объектов` : ""}
              </p>
            </div>
            <button
              onClick={() => setOpenAdd(true)}
              className="inline-flex items-center gap-2 self-start rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_15px_30px_rgba(99,102,241,0.25)] transition hover:translate-y-[-1px] hover:brightness-105"
            >
              <span className="text-lg leading-none">+</span>
              Добавить объект
            </button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat, idx) => (
              <div
                key={idx}
                className="rounded-3xl border border-white/70 bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur animate-softFade"
              >
                <p className="text-xs uppercase tracking-wide text-slate-400">{stat.label}</p>
                <div className="mt-2 text-2xl font-semibold text-slate-900">{stat.value}</div>
                <p className="mt-1 text-sm text-slate-500">{stat.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-6">
          <FilterBar
            hasActive={Boolean(qDraft || cityDraft || typeDraft)}
            onReset={resetFilters}
            onApply={applyFilters}
            applyDisabled={loading}
            className="shadow-[0_15px_35px_rgba(15,23,42,0.08)] border-white/80 bg-white/80"
          >
            <SearchInput
              value={qDraft}
              onChange={setQDraft}
              placeholder="Поиск по названию…"
              className="flex-[3] w-full"
            />
            <SelectField
              value={typeDraft}
              onChange={(v) => setTypeDraft(v as any)}
              placeholder="Тип"
              icon={<Building2 size={18} />}
              options={typeOptions}
              className="flex-[1] w-full"
            />
            <SelectField
              value={cityDraft}
              onChange={setCityDraft}
              placeholder="Город"
              icon={<MapPin size={18} />}
              options={citySelectOptions}
              className="flex-[1] w-full"
            />
          </FilterBar>

          <div className="space-y-6 rounded-3xl border border-white/70 bg-white/90 p-6 shadow-[0_15px_40px_rgba(15,23,42,0.08)] backdrop-blur">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-500">
                {loading
                  ? "Обновляем список объектов…"
                  : total
                    ? `Показаны ${rangeStart}\u2009–\u2009${rangeEnd} из ${total}`
                    : "Нет объектов для отображения"}
              </p>
              {hasActiveFilters && (
                <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-4 py-1.5 text-xs font-medium text-indigo-700">
                  Активные фильтры
                  <span className="hidden sm:block text-slate-500">
                    {[city || "Все города", type || "Все типы", q ? `Поиск: ${q}` : "Без поиска"].join(" · ")}
                  </span>
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4">
              {loading && (
                <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50/70 p-8 text-center text-slate-500 animate-pulse">
                  Загружаем объекты…
                </div>
              )}

              {!loading &&
                items.map(h => (
                  <Link
                    key={h.id}
                    className="group relative flex flex-col gap-4 rounded-3xl border border-slate-100 bg-white/90 p-5 shadow-[0_10px_28px_rgba(15,23,42,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(99,102,241,0.18)] md:flex-row md:items-center"
                    to={`/my/objects/${h.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="inline-grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-indigo-50 text-indigo-600 shadow-inner">
                        <Building2 size={24} />
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-slate-900">{h.name}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                          <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50/80 px-3 py-1 font-medium text-indigo-700">
                            <Building2 size={14} />
                            {h.type}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-600">
                            <MapPin size={14} />
                            {h.city}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-3 py-1 font-medium text-indigo-700">
                            Максимум ОТА: {h.ota_count ?? otaLimit ?? "—"}
                          </span>
                        </div>
                        {h.ota_links?.length ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {h.ota_links.map((link) => (
                              <a
                                key={`${h.id}-${link.label}-${link.url}`}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-100"
                                onClick={(event) => event.stopPropagation()}
                              >
                                {link.label}
                                <ExternalLink size={12} />
                              </a>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="ml-auto flex flex-col items-start gap-2 text-sm text-slate-500 sm:flex-row sm:items-center sm:gap-4">
                      <div className="text-xs uppercase tracking-wide text-slate-400">ID: {h.id}</div>
                      {h.url && (
                        <a
                          href={h.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded-2xl border border-slate-200 px-4 py-2 text-sm font-medium text-indigo-600 transition hover:bg-indigo-50/70"
                          onClick={(event) => event.stopPropagation()}
                        >
                          Открыть
                        </a>
                      )}
                    </div>
                  </Link>
                ))}

              {!loading && items.length === 0 && (
                <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50/70 p-10 text-center text-slate-500">
                  <div>Ничего не найдено</div>
                  {hasActiveFilters && (
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="mt-4 inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                      Сбросить фильтры
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-4 border-t border-slate-100 pt-6 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2">
                <label className="text-sm text-slate-500">На странице:</label>
                <select
                  value={perPage}
                  onChange={(e) => changePerPage(Number(e.target.value))}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-inner"
                >
                  {[10, 20, 50, 100].map(n => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-center gap-3">
                <button
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                >
                  Назад
                </button>
                <div className="rounded-2xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700">
                  {page} / {pages}
                </div>
                <button
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
                  onClick={() => setPage(p => Math.min(pages, p + 1))}
                  disabled={page >= pages || loading}
                >
                  Вперёд
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50/90 px-5 py-4 text-sm text-amber-800 shadow-inner">
                {error}
              </div>
            )}
          </div>
        </section>

        {companyId && (
          <AddHotelModal
            open={openAdd}
            onClose={() => setOpenAdd(false)}
            onCreated={reloadCurrent}
            companyId={companyId}
            citySuggestions={cityOptions}
            otaLimit={otaLimit}
          />
        )}
      </main>
    </div>
  );
}
